import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { logDev } from "@/lib/log";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BadgeCheck, MessageCircle, Users, LogIn, Camera, Save, ShieldCheck, Music2 } from "lucide-react";
import PushToggle from "@/components/PushToggle";
import UserDirectMessages from "@/components/UserDirectMessages";
import UserDmInbox from "@/components/UserDmInbox";
import MilestoneBadge from "@/components/MilestoneBadge";

const STATES_MY = ["Johor","Kedah","Kelantan","Melaka","Negeri Sembilan","Pahang","Perak","Perlis","Pulau Pinang","Sabah","Sarawak","Selangor","Terengganu","W.P. Kuala Lumpur","W.P. Labuan","W.P. Putrajaya"];
const EXPERIENCE = ["< 1 tahun", "1 tahun", "2 tahun", "3 tahun", "More"];

function LoggedOutProfile() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-10 text-left" data-testid="profile-logged-out">
      <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-slate-500">Profile</div>
      <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">Sign in to your account</h2>
      <p className="mt-3 text-slate-600 max-w-lg leading-relaxed">
        Login guna registered email dan Loyal Primus Client ID sebagai password. Belum register? Balik ke Home.
      </p>
      <div className="mt-6 flex gap-3">
        <Link to="/login" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-full text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }} data-testid="profile-login-btn">
          <LogIn size={14} /> Log in
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 border border-slate-200 hover:bg-white px-5 py-2.5 rounded-full text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }}>
          Register at Home
        </Link>
      </div>
    </div>
  );
}

function ProfilePicture({ user, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let url;
    (async () => {
      if (!user?.profile_picture_path) return;
      try {
        const res = await api.get(`/files/${user.profile_picture_path}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setPreview(url);
      } catch (err) { logDev(err); }
    })();
    return () => url && URL.revokeObjectURL(url);
  }, [user?.profile_picture_path]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("kind", "profile");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/profile", { profile_picture_path: data.path });
      toast.success("Profile picture updated.");
      onChanged && onChanged();
    } catch (_) { toast.error("Upload failed."); }
    finally { setBusy(false); }
  };

  const initial = (user?.name || user?.email || "U").charAt(0).toUpperCase();

  return (
    <div className="relative w-28 h-28">
      {preview ? (
        <img src={preview} alt="" className="w-28 h-28 rounded-2xl object-cover border border-slate-200" />
      ) : (
        <div className="w-28 h-28 rounded-2xl bg-slate-100 border border-slate-200 grid place-items-center font-display font-bold text-4xl text-slate-500">
          {initial}
        </div>
      )}
      <label className="absolute -bottom-2 -right-2 w-9 h-9 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] grid place-items-center cursor-pointer text-white shadow-lg" style={{ transition: "background-color 0.2s ease" }} data-testid="profile-upload-btn">
        <Camera size={16} />
        <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={busy} />
      </label>
    </div>
  );
}

function ProfileMilestone({ userId }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/quiz/milestones").then(({ data }) => setM(data?.[userId] || null)).catch(() => {});
  }, [userId]);
  return <MilestoneBadge milestone={m} testId="profile-milestone-badge" className="!text-[10px] !px-2 !py-1" />;
}

export default function ProfilePage() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", age: "", state: "", trading_experience: "", email: "", telegram_username: "", loyal_primus_client_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        age: user.age || "",
        state: user.state || "",
        trading_experience: user.trading_experience || "",
        email: user.email || "",
        telegram_username: user.telegram_username || "",
        loyal_primus_client_id: user.loyal_primus_client_id || "",
      });
    }
  }, [user]);

  if (!user) return <div className="max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-16"><LoggedOutProfile /></div>;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name || null,
        age: form.age ? parseInt(form.age, 10) : null,
        state: form.state || null,
        trading_experience: form.trading_experience || null,
      };
      // Only send identity fields if they actually changed so we don't hit
      // uniqueness checks unnecessarily.
      const trimmedEmail = (form.email || "").trim().toLowerCase();
      const trimmedTg = (form.telegram_username || "").trim().replace(/^@/, "");
      const trimmedLp = (form.loyal_primus_client_id || "").trim();
      if (user.role !== "admin") {
        if (trimmedEmail && trimmedEmail !== (user.email || "").toLowerCase()) payload.email = trimmedEmail;
        if (trimmedTg !== (user.telegram_username || "")) payload.telegram_username = trimmedTg;
        if (trimmedLp && trimmedLp !== (user.loyal_primus_client_id || "")) payload.loyal_primus_client_id = trimmedLp;
      }

      const { data } = await api.patch("/profile", payload);
      if (data?._password_changed) {
        toast.success("Profile saved. Password baru = Loyal Primus Client ID baru.");
      } else {
        toast.success("Profile saved.");
      }
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Save failed.";
      toast.error(detail);
    }
    finally { setBusy(false); }
  };

  const isApproved = user.status === "approved" || user.role === "admin";
  const isAdmin = user.role === "admin";

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="profile-page">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-[#00b4ff]">Account</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight">Profile</h1>
      </div>

      <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <ProfilePicture user={user} onChanged={refresh} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-xl sm:text-2xl tracking-tight min-w-0 [overflow-wrap:anywhere]">{user.name || (user.email || "").split("@")[0]}</div>
              <ProfileMilestone userId={user.id} />
              {isApproved && (
                <span className="inline-flex items-center gap-1 text-[#00b4ff] bg-[#00b4ff]/10 border border-[#00b4ff]/20 px-2.5 py-1 rounded-full text-[10px] font-mono-plex tracking-widest uppercase" data-testid="verification-badge">
                  <BadgeCheck size={12} /> Verified
                </span>
              )}
              {isAdmin && (
                <span className="inline-flex items-center gap-1 text-white bg-slate-900 px-2.5 py-1 rounded-full text-[10px] font-mono-plex tracking-widest uppercase">
                  <ShieldCheck size={12} /> Admin
                </span>
              )}
            </div>
            <div className="mt-1 text-sm text-slate-600 font-mono-plex break-all">{user.email}</div>
            {user.telegram_username && <div className="mt-0.5 text-xs text-slate-500 font-mono-plex">TG: @{user.telegram_username.replace(/^@/, "")}</div>}
            {user.loyal_primus_client_id && <div className="mt-0.5 text-xs text-slate-500 font-mono-plex">LP ID: {user.loyal_primus_client_id}</div>}
          </div>
        </div>

        <form onSubmit={save} className="mt-8 grid md:grid-cols-2 gap-5">
          <Field label="Nama">
            <input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff]" />
          </Field>
          <Field label="Umur">
            <input data-testid="profile-age" type="number" min="0" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff] font-mono-plex" />
          </Field>
          <Field label="Negeri">
            <select data-testid="profile-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff]">
              <option value="">Pilih negeri</option>
              {STATES_MY.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Pengalaman Trading">
            <select data-testid="profile-experience" value={form.trading_experience} onChange={(e) => setForm({ ...form, trading_experience: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff]">
              <option value="">Pilih pengalaman</option>
              {EXPERIENCE.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>

          {!isAdmin && (
            <>
              <div className="md:col-span-2 mt-2 border-t border-slate-100 pt-5">
                <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-slate-500">Login & Contact</div>
              </div>
              <Field label="Email">
                <input
                  data-testid="profile-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff] font-mono-plex text-sm"
                />
              </Field>
              <Field label="Telegram Username">
                <input
                  data-testid="profile-telegram"
                  value={form.telegram_username}
                  onChange={(e) => setForm({ ...form, telegram_username: e.target.value })}
                  placeholder="tanpa @"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff] font-mono-plex text-sm"
                />
              </Field>
              <Field label="Loyal Primus Client ID">
                <input
                  data-testid="profile-lp-id"
                  value={form.loyal_primus_client_id}
                  onChange={(e) => setForm({ ...form, loyal_primus_client_id: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff] font-mono-plex text-sm"
                />
                <div className="mt-1.5 text-[11px] font-mono-plex text-amber-700">
                  Nota: LP Client ID = password login anda. Bila ditukar, password baru = LP ID baru.
                </div>
              </Field>
            </>
          )}

          <div className="md:col-span-2">
            <button data-testid="profile-save-btn" disabled={busy} className="inline-flex items-center gap-2 bg-[#00b4ff] hover:bg-[#0090cc] text-white font-display font-semibold px-6 py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
              <Save size={14} /> {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>

      {/* Notifications */}
      <div className="mt-8">
        <PushToggle />
      </div>

      {/* Direct chat with admin — only for approved non-admin users */}
      {!isAdmin && isApproved && (
        <div className="mt-6">
          <UserDirectMessages />
        </div>
      )}

      {/* Peer-to-peer DM inbox — only for approved users */}
      {isApproved && (
        <div className="mt-6">
          <UserDmInbox />
        </div>
      )}

      {/* CTAs */}
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <a
          data-testid="support-btn"
          href="https://t.me/nanatradingsecret"
          target="_blank"
          rel="noreferrer"
          className="rounded-2xl border border-slate-800 bg-slate-900 text-white p-6 hover:border-[#00b4ff] hover:bg-slate-950 group"
          style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
        >
          <div className="w-10 h-10 rounded-lg bg-slate-800 grid place-items-center text-slate-200 group-hover:bg-[#00b4ff] group-hover:text-white" style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}>
            <MessageCircle size={18} />
          </div>
          <div className="mt-4 font-display font-semibold text-white">Contact Support</div>
          <div className="text-sm text-slate-300">Chat direct dengan admin di Telegram @nanatradingsecret</div>
        </a>
        <a
          data-testid="community-btn"
          href="https://t.me/tradingsecretmalaysia"
          target="_blank"
          rel="noreferrer"
          className="rounded-2xl border border-slate-800 bg-slate-900 text-white p-6 hover:border-[#00b4ff] hover:bg-slate-950 group"
          style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
        >
          <div className="w-10 h-10 rounded-lg bg-slate-800 grid place-items-center text-slate-200 group-hover:bg-[#00b4ff] group-hover:text-white" style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}>
            <Users size={18} />
          </div>
          <div className="mt-4 font-display font-semibold text-white">Join Telegram Community</div>
          <div className="text-sm text-slate-300">@tradingsecretmalaysia — daily insights & discussions</div>
        </a>
        <a
          data-testid="tiktok-btn"
          href="https://www.tiktok.com/@nabilguruforex"
          target="_blank"
          rel="noreferrer"
          className="rounded-2xl border border-slate-800 bg-slate-900 text-white p-6 hover:border-[#00b4ff] hover:bg-slate-950 group"
          style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
        >
          <div className="w-10 h-10 rounded-lg bg-slate-800 grid place-items-center text-slate-200 group-hover:bg-[#00b4ff] group-hover:text-white" style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}>
            <Music2 size={18} />
          </div>
          <div className="mt-4 font-display font-semibold text-white">Follow on TikTok</div>
          <div className="text-sm text-slate-300">@nabilguruforex — short-form market plays & tips</div>
        </a>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        {isAdmin && (
          <button
            data-testid="go-to-admin-btn"
            onClick={() => nav("/admin")}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-full text-sm font-semibold"
            style={{ transition: "background-color 0.2s ease" }}
          >
            <ShieldCheck size={14} /> Open Admin Panel
          </button>
        )}
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); nav("/"); }}
          className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 px-5 py-2.5 rounded-full text-sm font-semibold"
          style={{ transition: "background-color 0.2s ease" }}
        >
          Logout
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-mono-plex tracking-widest uppercase text-slate-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
